<template>
  <div class="start-conversation-form">
    <h1 class="global-h1 start-conversation-form__title">Start the Conversation</h1>

    <!-- Selected creator snippet -->
    <div class="selected-creator-snippet start-conversation-form__selected-creator-snippet">
      <h3 class="global-h3 selected-creator-snippet__title">
        {{ newCustomCollabWizardStore.invitedCreatorEmail ? "Invited creator" : "Selected creator" }}
      </h3>

      <div class="selected-creator-snippet__avatar-and-name">
        <AvatarWithFallback class="selected-creator-snippet__avatar" :src="`${contentBaseUri}/cms/images/profiles/${newCustomCollabWizardStore.selectedCreator?.profilePhoto}`" />
        <div class="global-h3 selected-creator-snippet__name">
          {{ newCustomCollabWizardStore.invitedCreatorEmail ? newCustomCollabWizardStore.invitedCreatorEmail : newCustomCollabWizardStore.selectedCreator?.name }}
        </div>
      </div>
    </div>
    <!-- / Selected creator snippet -->

    <!-- Message form -->
    <div class="message-form start-conversation-form__message-form">
      <h3 class="global-h3 message-form__title">Initial Message</h3>
      <div style="margin-bottom: 7px">Click the <span :style="{ background: placeholderCount > 0 ? 'rgba(200, 200, 0, 0.5)' : 'transparent' }">[PLACEHOLDERS]</span> to fill in the text.</div>

      <TextareaWithHighlightedPlaceholders
        :initialMessage="newCustomCollabWizardStore.initialMessage"
        @focus="handleMessageTextareaFocus"
        @input="handleMessageTextareaInput"
        @blur="$event => (newCustomCollabWizardStore.initialMessage = $event.target.innerText)"
        @update:placeholderCount="placeholderCount = $event"
      />
      <div v-if="isTemplatePlaceholdersErrorVisible" style="margin: 10px 0 20px" class="global-error-string">Please fill the [PLACEHOLDERS]</div>
      <div v-if="isEmptyInitMsgErrorVisible" style="margin: 10px 0 20px" class="global-error-string">Please write an initial message</div>

      <LinkWithIcon class="message-form__ai-assist-link" isDottedUnderline color="orange" @click="isAiAssistFormVisible = !isAiAssistFormVisible">
        <template #icon><IconEmbedded name="ai_2-5" :size="16" /></template>
        <span>{{ isAiAssistFormVisible ? "Close" : "AI Assist" }}</span>
      </LinkWithIcon>

      <TransitionedHeightAuto :isOpened="isAiAssistFormVisible" isKeepAlive>
        <!-- AI assist form -->
        <div
          :class="{
            'ai-assist-form': true,
            'ai-assist-form--disabled': props.isSaving,
            'message-form__ai-assist-form': true,
          }"
        >
          <div class="ai-assist-form__title-and-textarea">
            <div class="global-h5 ai-assist-form__title">Guidance</div>
            <textarea
              class="global-textarea ai-assist-form__textarea"
              placeholder="What kind of message would you like? (e.g. 'Make it more professional' or 'Add more details about compensation')"
              v-model="aiGuidance"
              :disabled="props.isSaving"
            ></textarea>
          </div>

          <SrpButton class="ai-assist-form__confirm-button" style="margin-bottom: 20px" fill="outlined" color="teal" size="small" @click="generateAISuggestion" :isDisabled="isGeneratingAISuggestion">
            <template v-if="isGeneratingAISuggestion" #icon><LoadingIcon :size="17" /></template>
            <template v-else #icon><IconEmbedded name="ai_2-5" :size="17" /></template>
            {{ aiSuggestion ? "Generate Again" : "Generate Suggestion" }}
          </SrpButton>

          <div class="ai-assist-form__title-and-textarea">
            <div class="global-h5 ai-assist-form__title">AI Suggestion</div>
            <div class="global-textarea ai-assist-form__textarea" style="height: auto">
              {{ aiSuggestion ? aiSuggestion : "Suggestion will show here" }}
            </div>
          </div>

          <div class="ai-assist-form__buttons-panel">
            <SrpButton class="ai-assist-form__confirm-button" fill="outlined" color="teal" size="small" :isDisabled="!aiSuggestion || isAISuggestionTextCopied" @click="copyAISuggestionText">
              <template #icon>
                <IconEmbedded v-if="isAISuggestionTextCopied" name="check_2-5" :size="17" />
                <IconEmbedded v-else name="copy_2-5" :size="17" />
              </template>
              {{ isAISuggestionTextCopied ? "Copied" : "Copy" }}
            </SrpButton>

            <SrpButton class="ai-assist-form__confirm-button" fill="outlined" color="teal" size="small" :isDisabled="!aiSuggestion || isAISuggestionTextCopied" @click="useAISuggestionText">
              <template #icon>
                <IconEmbedded name="arrow-top_2-5" :size="17" />
              </template>
              Use This
            </SrpButton>
          </div>
        </div>
        <!-- / AI assist form -->
      </TransitionedHeightAuto>
    </div>
    <!-- / Message form -->
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import CustomerCustomCollabsRepo from "@repos/CustomerCustomCollabsRepo";
import { useRoute } from "vue-router";

// Components
import AvatarWithFallback from "@components/AvatarWithFallback.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import TransitionedHeightAuto from "@components/ui/TransitionedHeightAuto.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

// Global variables & injections
const contentBaseUri = globalThis.Bootstrap.Config.contentCdnBaseUri;

// Stores
import { useNewCustomCollabWizardStore } from "@stores/newCustomCollabWizardStore";
import TextareaWithHighlightedPlaceholders from "@views/CustomCollabs/CustomerNewCustomCollabWizard/TextareaWithHighlightedPlaceholders.vue";
const newCustomCollabWizardStore = useNewCustomCollabWizardStore();

// Router
const route = useRoute();

const props = withDefaults(
  defineProps<{
    isSaving: boolean;
  }>(),
  {
    isSaving: false,
  }
);

// AI guidance & suggestion ===================================================
const isAiAssistFormVisible = ref<boolean>(false);

const aiGuidance = ref<string>("");
const aiSuggestion = ref<string>("");

const isGeneratingAISuggestion = ref<boolean>(false);

async function generateAISuggestion() {
  isGeneratingAISuggestion.value = true;
  aiSuggestion.value = await CustomerCustomCollabsRepo.improveInitialMsgWithAI(String(route.params.communityId), newCustomCollabWizardStore.initialMessage, aiGuidance.value);
  isGeneratingAISuggestion.value = false;
}

// Fill the initial message based on the selected template ====================
watch(
  () => newCustomCollabWizardStore.selectedTemplateId,
  () => {
    const selectedTemplate = newCustomCollabWizardStore.customCollabTemplatesList.find(t => t.templateId === newCustomCollabWizardStore.selectedTemplateId);
    newCustomCollabWizardStore.initialMessage = selectedTemplate.messageText;
  }
);

// Copy AI suggestion text ====================================================
const isAISuggestionTextCopied = ref<boolean>(false);

function copyAISuggestionText() {
  navigator.clipboard.writeText(aiSuggestion.value).then(
    () => console.info("Copied to clipboard: " + aiSuggestion.value),
    err => console.error("Failed to copy to clipboard: ", err)
  );

  isAISuggestionTextCopied.value = true;
  setTimeout(() => (isAISuggestionTextCopied.value = false), 3000);
}

// Use AI suggestion text ====================================================
function useAISuggestionText() {
  newCustomCollabWizardStore.initialMessage = aiSuggestion.value;
}

// Toggle validation notes ====================================================
const isTemplatePlaceholdersErrorVisible = ref<boolean>(false);
const isEmptyInitMsgErrorVisible = ref<boolean>(false);

const placeholderCount = ref<number>(0);

function handleMessageTextareaFocus() {
  isTemplatePlaceholdersErrorVisible.value = false;
  isEmptyInitMsgErrorVisible.value = false;
}

function handleMessageTextareaInput() {
  isTemplatePlaceholdersErrorVisible.value = false;
  isEmptyInitMsgErrorVisible.value = false;
}

// Define expose ==============================================================
defineExpose({
  showTemplatePlaceholdersError: () => (isTemplatePlaceholdersErrorVisible.value = true),
  showEmptyInitMsgError: () => (isEmptyInitMsgErrorVisible.value = true),
});
</script>

<style scoped lang="scss">
// Selected creator snippet ===================================================
.selected-creator-snippet {
  min-height: 100px;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 0;

  &::before {
    content: "";
    width: calc(100% + 60px);
    height: 100%;
    position: absolute;
    inset: 0 auto auto -30px;
    z-index: -1;
    background: rgba(0, 0, 0, 0.05);
  }

  &__title {
    margin-right: 28px;
  }

  &__avatar-and-name {
    display: flex;
    align-items: center;
  }

  &__avatar {
    width: 55px;
    height: 55px;
    margin-right: 10px;
  }

  &__name {
    font-weight: 500;
  }
}

// AI assis form ==============================================================
.ai-assist-form {
  padding: 20px 20px;
  border-radius: 4px;
  position: relative;
  z-index: 0;
  background: rgba(0, 0, 0, 0.05);

  &::before {
    content: "";
    width: 20px;
    height: 20px;
    position: absolute;
    inset: -6px auto auto 28px;
    z-index: -1;
    transform: rotate(45deg);
    background: rgba(240, 240, 240, 1);
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__title-and-textarea {
    margin-bottom: 18px;
  }

  &__title {
    margin-bottom: 8px;
  }

  &__textarea {
    height: 100px;
    white-space: pre-wrap;
  }

  &__buttons-panel {
    gap: 12px;
    display: flex;
    flex-wrap: wrap;
  }

  &__confirm-button {
  }
}

// Message form ===============================================================
.message-form {
  &__title {
    margin-bottom: 5px;
  }

  &__textarea {
    margin-bottom: 8px;
  }

  &__ai-assist-link {
    margin-left: -5px;
    margin-top: 8px;
  }

  &__ai-assist-form {
    margin-top: 15px;
  }
}

// Start the conversation form ================================================
.start-conversation-form {
  &__title {
    margin-bottom: 23px;
  }

  &__selected-creator-snippet {
    margin-bottom: 27px;
  }

  &__message-form {
  }
}
</style>
